<script>
/**
 * 상품 인출 상세 — 통합원장 기준 인출완료 처리.
 */
import { getProductWithdrawalDetail, putProductWithdrawalWithdrawn } from '@/api/productWithdrawal.js'

export default {
  name: 'ProductWithdrawalDetailPage',
  data() {
    return {
      paymentCode: this.$route.params.paymentCode,
      detail: null,
      loading: false,
      actionLoading: false,
    }
  },
  mounted() {
    this.fetchDetail()
  },
  watch: {
    '$route.params.paymentCode'(code) {
      this.paymentCode = code
      this.fetchDetail()
    },
  },
  methods: {
    async fetchDetail() {
      if (!this.paymentCode) {
        this.$router.push('/order-status')
        return
      }
      this.loading = true
      try {
        const res = await getProductWithdrawalDetail(this.paymentCode)
        if (res.data?.code === 0) {
          this.detail = res.data.data
        } else {
          this.$router.push('/order-status')
        }
      } catch (e) {
        console.error('[ProductWithdrawalDetailPage]', e)
        this.$router.push('/order-status')
      } finally {
        this.loading = false
      }
    },
    goBack() {
      this.$router.push('/order-status')
    },
    formatDate(val) {
      if (!val) return '-'
      return String(val).substring(0, 10)
    },
    formatDateTime(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },
    formatAmount(val) {
      if (val == null || val === '') return '-'
      return Number(val).toLocaleString()
    },
    async onClickWithdrawn() {
      if (!this.detail || this.detail.withdrawnYn === 'Y') return
      if (!window.confirm('인출완료로 처리하시겠습니까? 통합원장에 반영됩니다.')) return
      this.actionLoading = true
      try {
        const res = await putProductWithdrawalWithdrawn(this.paymentCode)
        if (res.data?.code === 0) {
          await this.fetchDetail()
        } else {
          window.alert(res.data?.message || '처리에 실패했습니다.')
        }
      } catch (e) {
        console.error(e)
        window.alert('요청 중 오류가 발생했습니다.')
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
          <span class="breadcrumb-link text-primary" @click="goBack">인출하기</span>
          <v-icon size="12">mdi-chevron-right</v-icon>
          <span>상세</span>
        </div>
        <div class="text-h5 font-weight-bold">상품 인출 상세</div>
        <div class="text-body-2 text-grey mt-1">상품구매 결제·인출 예약 정보</div>
      </div>
    </div>

    <!-- 로딩 스켈레톤 -->
    <v-row v-if="loading">
      <v-col v-for="i in 3" :key="i" cols="12" md="6">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <template v-else-if="detail">
      <v-row>
        <!-- 1. 회원 정보 -->
        <v-col cols="12">
          <v-card rounded="xl" elevation="0" class="detail-card mb-4">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-primary-soft">
                <v-icon color="primary" size="18">mdi-account-outline</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">회원 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">이름</div>
                  <div class="dl-value font-weight-bold">{{ detail.userName || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">연락처</div>
                  <div class="dl-value">{{ detail.userPhone || '-' }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- 2. 결제·인출 정보 -->
        <v-col cols="12">
          <v-card rounded="xl" elevation="0" class="detail-card mb-4">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-amber-soft">
                <v-icon color="amber-darken-2" size="18">mdi-cash-register</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">결제·인출 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">통합원장</div>
                  <div class="dl-value text-caption text-medium-emphasis font-weight-medium">{{ detail.orderCode || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">결제금액</div>
                  <div class="dl-value font-weight-bold">{{ formatAmount(detail.price) }}원</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">인출 지점</div>
                  <div class="dl-value">
                    <template v-if="detail.branchName">
                      <span class="font-weight-medium">{{ detail.branchName }}</span>
                      <span v-if="detail.branchCode" class="text-caption text-medium-emphasis ml-1">({{ detail.branchCode }})</span>
                    </template>
                    <span v-else-if="detail.branchCode">{{ detail.branchCode }}</span>
                    <span v-else class="text-medium-emphasis">-</span>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">인출 예약일</div>
                  <div class="dl-value">{{ formatDate(detail.reserveDate) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">결제일시</div>
                  <div class="dl-value text-medium-emphasis">{{ formatDateTime(detail.insertDt) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">인출 상태</div>
                  <div class="dl-value d-flex align-center flex-wrap ga-2">
                    <v-chip
                      size="x-small"
                      label
                      variant="tonal"
                      class="font-weight-medium"
                      :color="detail.withdrawnYn === 'Y' ? 'success' : 'warning'"
                    >
                      {{ detail.withdrawnYn === 'Y' ? '인출완료' : '미인출' }}
                    </v-chip>
                    <span v-if="detail.withdrawnYn === 'Y'" class="text-caption text-medium-emphasis">
                      {{ formatDateTime(detail.withdrawnDt) }}
                    </span>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- 3. 상품 정보 -->
        <v-col cols="12">
          <v-card rounded="xl" elevation="0" class="detail-card mb-4">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-teal-soft">
                <v-icon color="teal" size="18">mdi-package-variant</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">상품 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">상품코드</div>
                  <div class="dl-value text-caption text-medium-emphasis font-weight-medium">{{ detail.productCode || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">상품명</div>
                  <div class="dl-value font-weight-medium">{{ detail.productName || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">현물 구분</div>
                  <div class="dl-value">
                    <v-chip size="x-small" color="primary" variant="tonal" label class="font-weight-medium">
                      {{ detail.spotTypeLabel || '-' }}
                    </v-chip>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">중량</div>
                  <div class="dl-value">{{ detail.weightG != null ? detail.weightG : '-' }}g</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- 인출완료 처리 -->
        <v-col cols="12">
          <div v-if="detail.withdrawnYn !== 'Y'" class="d-flex justify-end">
            <v-btn
              color="primary"
              prepend-icon="mdi-check-circle-outline"
              :loading="actionLoading"
              :disabled="!detail.orderCode"
              @click="onClickWithdrawn"
            >
              인출완료 처리
            </v-btn>
          </div>
          <v-alert
            v-if="detail.withdrawnYn !== 'Y' && !detail.orderCode"
            type="warning"
            variant="tonal"
            class="mt-4"
            density="compact"
          >
            통합원장이 없으면 인출완료를 처리할 수 없습니다.
          </v-alert>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<style scoped>
/* ── 공통 카드 (TradeDetailPage와 동일) ── */
.detail-card {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.detail-card-header {
  background: rgba(0, 0, 0, 0.018);
}

/* ── 헤더 아이콘 ── */
.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}
.bg-primary-soft { background: rgba(var(--v-theme-primary), 0.1); }
.bg-amber-soft  { background: rgba(255, 193, 7, 0.12); }
.bg-teal-soft   { background: rgba(0, 150, 136, 0.1); }

/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* ── 정의 리스트 ── */
.dl-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 16px;
}

.dl-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dl-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.38);
  text-transform: uppercase;
}

.dl-value {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.82);
  line-height: 1.4;
}
</style>
